
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Services() {
  const { t } = useTranslation('home');

  const services = [
    {
      icon: 'ri-fire-line',
      titleKey: 'services.naturalGas.title',
      descriptionKey: 'services.naturalGas.description',
      featuresKey: 'services.naturalGas.features',
      link: '/services/natural-gas'
    },
    {
      icon: 'ri-flashlight-line',
      titleKey: 'services.electricity.title',
      descriptionKey: 'services.electricity.description',
      featuresKey: 'services.electricity.features',
      link: '/services/electricity'
    },
    {
      icon: 'ri-building-line',
      titleKey: 'services.infrastructure.title',
      descriptionKey: 'services.infrastructure.description',
      featuresKey: 'services.infrastructure.features',
      link: '/services/infrastructure'
    },
    {
      icon: 'ri-shield-check-line',
      titleKey: 'services.compliance.title',
      descriptionKey: 'services.compliance.description',
      featuresKey: 'services.compliance.features',
      link: '/services/compliance'
    }
  ];

  const getFeatures = (featuresKey: string): string[] => {
    try {
      const features = t(featuresKey, { returnObjects: true });
      return Array.isArray(features) ? features : [];
    } catch (error) {
      console.warn(`Failed to load features for key: ${featuresKey}`, error);
      return [];
    }
  };

  return (
    <section className="py-20 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0D1B2A] mb-4 tracking-tight">
            {t('services.title', 'Our Services')}
          </h2>
          <p className="text-xl text-[#394B59] max-w-3xl mx-auto leading-relaxed">
            {t('services.subtitle', 'Comprehensive solutions for your energy needs')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const features = getFeatures(service.featuresKey);
            return (
              <div 
                key={`service-${index}`}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 group cursor-pointer"
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-[#0D1B2A] rounded-xl flex items-center justify-center group-hover:bg-[#2E8BC0] transition-colors duration-300">
                      <i className={`${service.icon} text-2xl text-white`}></i>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#0D1B2A] mb-3 group-hover:text-[#2E8BC0] transition-colors duration-300">
                      {t(service.titleKey, `Service ${index + 1}`)}
                    </h3>
                    
                    <p className="text-[#394B59] mb-6 leading-relaxed">
                      {t(service.descriptionKey, 'Professional service with quality assurance')}
                    </p>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {features.map((feature, featureIndex) => (
                        <div key={`feature-${featureIndex}`} className="flex items-center space-x-2">
                          <i className="ri-check-line text-[#2E8BC0] text-sm"></i>
                          <span className="text-sm text-[#394B59]">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link 
                      to={service.link}
                      className="inline-flex items-center text-[#2E8BC0] hover:text-[#0D1B2A] font-medium transition-colors duration-200 cursor-pointer"
                    >
                      {t('services.learnMore', 'Learn More')}
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
